import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, UserRound, MapPin, Coffee, Dumbbell, Plus, Trash2, Star, RotateCcw, ArrowLeft, Repeat, Award, Compass, Send, KeyRound } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/site/PasswordInput";
import { API_BASE } from "@/lib/api";

const api = (opts = {}) => axios.create({ baseURL: API_BASE, withCredentials: true, ...opts });
const EMPTY_ADDR = { label: "Home", line1: "", line2: "", city: "", state: "", pincode: "", country: "India", phone: "" };
const BLENDS = [["barrel_aged", "Barrel Aged"], ["fermented", "Fermented"], ["arabica", "100% Arabica"], ["robusta", "100% Robusta"]];
const ROASTS = ["light", "medium", "dark"];
const GRINDS = [["whole", "Whole Bean"], ["coarse", "French Press"], ["pourover", "Pour Over"], ["fine", "Espresso (Fine)"]];
const SIZES = [["250", "250 g"], ["500", "500 g"], ["1000", "1 kg"], ["5000", "5 kg"]];
const FREQS = ["weekly", "fortnightly", "monthly"];
const selCls = "w-full h-11 rounded-xl border border-[#E5E2D9] bg-[#F7F5F0] px-3 text-sm text-[#2D372B]";

function SubscriptionSection({ user }) {
  const [subs, setSubs] = useState(null);
  const [form, setForm] = useState({ speciality: "arabica", roast: "medium", grind: "whole", size: "250", frequency: "monthly" });
  const [saving, setSaving] = useState(false);
  const load = () => api().get("/member/subscriptions").then((r) => setSubs(r.data)).catch(() => setSubs([]));
  useEffect(() => { load(); }, []); // eslint-disable-line
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const create = async () => {
    setSaving(true);
    try {
      await api().post("/member/subscriptions", form);
      toast.success("Subscription started — Coach Sanjay has been notified!");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create subscription");
    } finally {
      setSaving(false);
    }
  };
  const cancel = async (id) => {
    await api().post(`/member/subscriptions/${id}/cancel`).catch(() => {});
    load();
  };
  return (
    <section className="bg-white border border-[#E5E2D9] rounded-3xl p-7">
      <div className="flex items-center gap-2 mb-1"><Repeat size={16} className="text-[#89937C]" /><h2 className="font-serif-display text-xl text-[#2D372B]">Coffee subscription</h2></div>
      <p className="text-xs text-[#5C665A] mb-4">Never run out — we'll prepare your blend every cycle and send you a payment link before each delivery.</p>
      {(subs || []).filter((s) => s.status === "active").map((s) => (
        <div key={s.id} data-testid="subscription-row" className="rounded-2xl border border-[#89937C] bg-[#89937C]/5 p-4 mb-3 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-sm font-medium text-[#2D372B] capitalize">{s.speciality.replace("_", " ")} · {s.roast} · {Number(s.size) >= 1000 ? `${s.size / 1000} kg` : `${s.size} g`} · <span className="text-[#89937C]">{s.frequency}</span></div>
            <div className="text-xs text-[#5C665A] mt-0.5">Next delivery ~ {s.next_delivery}</div>
          </div>
          <button data-testid="subscription-cancel-btn" onClick={() => cancel(s.id)} className="text-xs text-[#AD6A48] hover:underline">Cancel</button>
        </div>
      ))}
      <div className="grid sm:grid-cols-2 gap-2.5">
        <select data-testid="sub-blend" value={form.speciality} onChange={set("speciality")} className={selCls}>{BLENDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        <select data-testid="sub-roast" value={form.roast} onChange={set("roast")} className={selCls}>{ROASTS.map((r) => <option key={r} value={r}>{`${r[0].toUpperCase()}${r.slice(1)} roast`}</option>)}</select>
        <select data-testid="sub-grind" value={form.grind} onChange={set("grind")} className={selCls}>{GRINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        <select data-testid="sub-size" value={form.size} onChange={set("size")} className={selCls}>{SIZES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        <select data-testid="sub-frequency" value={form.frequency} onChange={set("frequency")} className={`${selCls} sm:col-span-2`}>{FREQS.map((f) => <option key={f} value={f}>{`${f[0].toUpperCase()}${f.slice(1)} delivery`}</option>)}</select>
      </div>
      <button data-testid="sub-create-btn" onClick={create} disabled={saving} className="mt-4 inline-flex items-center gap-2 rounded-full px-6 py-2.5 bg-[#2D372B] text-[#F7F5F0] text-sm font-medium hover:bg-[#89937C] transition-colors disabled:opacity-60">
      {saving ? <Loader2 size={13} className="animate-spin" /> : <Repeat size={13} />} Start subscription
      </button>
    </section>
  );
}

function RequestsSection() {
  const [requests, setRequests] = useState(null);
  useEffect(() => { api().get("/member/requests").then((r) => setRequests(r.data)).catch(() => setRequests([])); }, []);
  const badge = {
    new: "bg-[#5C665A]/10 text-[#5C665A]", contacted: "bg-[#89937C]/15 text-[#737D67]",
    quoted: "bg-[#C57B57]/15 text-[#AD6A48]", won: "bg-[#2D372B]/10 text-[#2D372B]", lost: "bg-[#A05252]/10 text-[#A05252]",
  };
  const label = { new: "Received", contacted: "In conversation", quoted: "Quote sent", won: "Confirmed", lost: "Closed" };
  if (!requests || requests.length === 0) return null;
  return (
    <section className="bg-white border border-[#E5E2D9] rounded-3xl p-7" data-testid="requests-section">
      <div className="flex items-center gap-2 mb-1"><Send size={15} className="text-[#AD6A48]" /><h2 className="font-serif-display text-xl text-[#2D372B]">My booking requests</h2></div>
      <p className="text-xs text-[#5C665A] mb-4">Journeys you've asked Coach Sanjay to arrange, and where each one stands.</p>
      <div className="space-y-3">
        {requests.map((r) => (
          <div key={r.id} data-testid="request-row" className="rounded-2xl border border-[#E5E2D9] p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <Link to={`/journey/${r.journey_id}`} className="text-sm font-medium text-[#2D372B] hover:text-[#AD6A48] transition-colors">{r.journey_title}</Link>
              <div className="text-xs text-[#5C665A] mt-0.5">{r.days} days · travelling {r.travel_month || "flexible"} · sent {new Date(r.created_at).toLocaleDateString()}</div>
            </div>
            <span data-testid="request-status-badge" className={`text-xs font-semibold uppercase tracking-wider rounded-full px-3 py-1.5 ${badge[r.status] || badge.new}`}>
              {label[r.status] || r.status}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

function JourneysSection() {
  const [journeys, setJourneys] = useState(null);
  useEffect(() => { api().get("/member/journeys").then((r) => setJourneys(r.data)).catch(() => setJourneys([])); }, []);
  return (
    <section className="bg-white border border-[#E5E2D9] rounded-3xl p-7">
      <div className="flex items-center gap-2 mb-1"><Compass size={16} className="text-[#89937C]" /><h2 className="font-serif-display text-xl text-[#2D372B]">My India journeys</h2></div>
      <p className="text-xs text-[#5C665A] mb-4">Journeys crafted by the AI planner while signed in are saved here.</p>
      {!journeys ? (
        <Loader2 size={16} className="animate-spin text-[#89937C]" />
      ) : journeys.length === 0 ? (
        <Link to="/journey" data-testid="journeys-empty-cta" className="inline-flex items-center gap-2 text-sm text-[#89937C] hover:text-[#2D372B]"><Compass size={14} /> Plan your first wellness journey →</Link>
      ) : (
        <div className="space-y-3">
          {journeys.map((j) => (
            <Link key={j.id} to={`/journey/${j.id}`} data-testid="journey-row" className="block rounded-2xl border border-[#E5E2D9] p-4 hover:border-[#89937C] transition-colors">
              <div className="text-sm font-medium text-[#2D372B]">{j.title}</div>
              <div className="text-xs text-[#5C665A] mt-0.5">{j.days} days · {(j.destinations || []).join(" → ")}</div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

function LoyaltySection() {
  const [data, setData] = useState(null);
  useEffect(() => { api().get("/member/loyalty").then((r) => setData(r.data)).catch(() => setData({ points: 0, ledger: [] })); }, []);
  return (
    <section className="bg-[#2D372B] rounded-3xl p-7 text-[#F7F5F0]">
      <div className="flex items-center gap-2 mb-1"><Award size={16} className="text-[#D19B7B]" /><h2 className="font-serif-display text-xl">Loyalty points</h2></div>
      <div data-testid="loyalty-points" className="font-serif-display text-5xl mt-2">{data ? data.points : "…"}<span className="text-lg text-[#C9CFC0]"> pts</span></div>
      <p className="text-xs text-[#C9CFC0] mt-2">Earn 1 point per ₹100 spent + 50 bonus points on every re-order. 1 point = ₹1 off at coffee checkout.</p>
      {data && data.ledger.length > 0 && (
        <div className="mt-4 space-y-1.5">
          {data.ledger.slice(0, 5).map((l, i) => (
            <div key={i} className="flex justify-between text-xs text-[#C9CFC0]">
              <span>{l.reason}</span>
              <span className={l.points > 0 ? "text-[#A9B39D]" : "text-[#D19B7B]"}>{l.points > 0 ? "+" : ""}{l.points}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function AddressForm({ onSaved }) {
  const [a, setA] = useState(EMPTY_ADDR);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setA((x) => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    if (!a.line1.trim() || !a.city.trim() || !a.pincode.trim()) return toast.error("Address line, city and pincode are required.");
    setSaving(true);
    try {
      const r = await api().post("/member/addresses", a);
      onSaved(r.data);
      setA(EMPTY_ADDR);
      toast.success("Address saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save address");
    } finally {
      setSaving(false);
    }
  };
  const cls = "bg-[#F7F5F0] border-[#E5E2D9] h-11";
  return (
    <div className="grid sm:grid-cols-2 gap-2.5 mt-4">
      <Input data-testid="addr-label" value={a.label} onChange={set("label")} placeholder="Label (Home / Office / Mom's place)" className={cls} />
      <Input data-testid="addr-phone" value={a.phone} onChange={set("phone")} placeholder="Contact number" className={cls} />
      <Input data-testid="addr-line1" value={a.line1} onChange={set("line1")} placeholder="Address line 1 *" className={`${cls} sm:col-span-2`} />
      <Input data-testid="addr-line2" value={a.line2} onChange={set("line2")} placeholder="Address line 2" className={`${cls} sm:col-span-2`} />
      <Input data-testid="addr-city" value={a.city} onChange={set("city")} placeholder="City *" className={cls} />
      <Input data-testid="addr-state" value={a.state} onChange={set("state")} placeholder="State" className={cls} />
      <Input data-testid="addr-pincode" value={a.pincode} onChange={set("pincode")} placeholder="Pincode *" className={cls} />
      <Input data-testid="addr-country" value={a.country} onChange={set("country")} placeholder="Country" className={cls} />
      <button data-testid="addr-save-btn" onClick={save} disabled={saving} className="sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 bg-[#2D372B] text-[#F7F5F0] text-sm font-medium hover:bg-[#89937C] transition-colors disabled:opacity-60">
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Save address
      </button>
    </div>
  );
}

function GlobalSeriesStrip() {
  const [cuisines, setCuisines] = useState(null);
  useEffect(() => { api().get("/diet/global-series").then((r) => setCuisines(r.data)).catch(() => setCuisines([])); }, []);
  if (!cuisines || cuisines.length === 0) return null;
  return (
    <section className="bg-white border border-[#E5E2D9] rounded-3xl p-7" data-testid="global-series-strip">
      <div className="flex items-center justify-between gap-3 mb-1">
        <h2 className="font-serif-display text-xl text-[#2D372B]">JAY 7™ Global Series</h2>
        <Link to="/diet/global-series" className="text-xs text-[#C57B57] font-medium hover:underline" data-testid="global-series-all">View all →</Link>
      </div>
      <p className="text-xs text-[#5C665A] mb-4">7-day fat-loss meal plans from 7 world cuisines — calorie-tracked, with grocery-list PDF.</p>
      <div className="flex gap-3 overflow-x-auto pb-1">
        {cuisines.map((c) => (
          <Link key={c.id} to={`/diet/global-series?c=${c.id}`} data-testid={`gs-card-${c.id}`}
            className="group shrink-0 w-36 rounded-2xl overflow-hidden border border-[#E5E2D9] hover:border-[#C57B57] transition-colors">
            <div className="relative h-20 overflow-hidden">
              <img src={c.image} alt={c.short} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 to-transparent" />
              <div className="absolute bottom-1.5 left-2 text-white text-xs font-serif-display">{c.short}</div>
            </div>
            <div className="px-2 py-1.5 text-[10px] text-[#5C665A]">~{c.avg_kcal} kcal/day · 7 days</div>
          </Link>
        ))}
      </div>
    </section>
  );
}

const PasswordSection = () => {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [waPref, setWaPref] = useState(null);

  useEffect(() => {
    api().get("/member/whatsapp-pref").then((r) => setWaPref(r.data)).catch(() => {});
  }, []);

  const toggleWa = async () => {
    const next = !waPref.opt_out;
    try {
      await api().post("/member/whatsapp-pref", { opt_out: next });
      setWaPref({ ...waPref, opt_out: next });
      toast.success(next ? "WhatsApp updates turned OFF — you'll only receive emails" : "WhatsApp updates turned ON");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update preference");
    }
  };

  const save = async () => {
    if (nw.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (nw !== confirm) { toast.error("New passwords don't match"); return; }
    setSaving(true);
    try {
      await api().post("/member/auth/change-password", { current_password: cur || null, new_password: nw });
      toast.success("Password updated — use it next time you sign in");
      setCur(""); setNw(""); setConfirm("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update password");
    } finally { setSaving(false); }
  };

  return (
    <section className="bg-white border border-[#E5E2D9] rounded-3xl p-7" data-testid="password-section">
      <div className="flex items-center gap-2 mb-1"><KeyRound size={16} className="text-[#89937C]" /><h2 className="font-serif-display text-xl text-[#2D372B]">Password & security</h2></div>
      <p className="text-xs text-[#5C665A] mb-4">Change your password any time. Signed up with Google? Leave "current password" empty to set one — then you can log in either way.</p>
      <div className="grid sm:grid-cols-3 gap-3">
        <PasswordInput data-testid="pw-current" value={cur} onChange={(e) => setCur(e.target.value)} placeholder="Current password" className="bg-[#F7F5F0] border-[#E5E2D9] h-11" />
        <PasswordInput data-testid="pw-new" value={nw} onChange={(e) => setNw(e.target.value)} placeholder="New password (min 6)" className="bg-[#F7F5F0] border-[#E5E2D9] h-11" />
        <PasswordInput data-testid="pw-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm new password" className="bg-[#F7F5F0] border-[#E5E2D9] h-11" />
      </div>
      <button data-testid="pw-save-btn" onClick={save} disabled={saving} className="mt-4 inline-flex items-center gap-2 rounded-full px-6 py-2.5 bg-[#2D372B] text-[#F7F5F0] text-sm font-medium hover:bg-[#89937C] transition-colors disabled:opacity-60">
        {saving && <Loader2 size={13} className="animate-spin" />} Update password
      </button>
      {waPref && waPref.has_phone && (
        <div className="mt-6 pt-5 border-t border-[#F0EEE6] flex items-center justify-between gap-3" data-testid="whatsapp-pref-row">
          <div>
            <div className="text-sm font-medium text-[#2D372B]">WhatsApp updates</div>
            <p className="text-xs text-[#5C665A] mt-0.5">Plan alerts, review reminders and order updates on WhatsApp. Turn off anytime — emails continue.</p>
          </div>
          <button
            type="button" role="switch" aria-checked={!waPref.opt_out} data-testid="whatsapp-pref-toggle" onClick={toggleWa}
            className={`relative w-12 h-6 rounded-full transition-colors shrink-0 ${waPref.opt_out ? "bg-[#D5D2C9]" : "bg-[#34A853]"}`}
          >
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${waPref.opt_out ? "left-0.5" : "left-6"}`} />
          </button>
        </div>
      )}
    </section>
  );
};

export default function MemberDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(!location.state?.user);
  const [orders, setOrders] = useState(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [dob, setDob] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [showAddrForm, setShowAddrForm] = useState(false);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return;
    api().get("/member/auth/me")
      .then((r) => { setUser(r.data); setChecking(false); })
      .catch(() => {
        if (!location.state?.user) navigate("/login", { replace: true });
        else setChecking(false);
      });
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!user) return;
    setName(user.name || "");
    setPhone(user.phone || "");
    setDob(user.dob || "");
    api().get("/member/orders").then((r) => setOrders(r.data)).catch(() => setOrders({ coffee: [], bootcamps: [] }));
  }, [user?.user_id]); // eslint-disable-line

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const r = await api().put("/member/profile", { name, phone, dob });
      setUser(r.data);
      toast.success("Profile updated");
    } catch {
      toast.error("Could not update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const logout = async () => {
    await api().post("/member/auth/logout").catch(() => {});
    navigate("/");
  };

  const setDefault = async (aid) => {
    const r = await api().post(`/member/addresses/${aid}/default`);
    setUser(r.data);
    toast.success("Default shipping address updated");
  };

  const deleteAddr = async (aid) => {
    const r = await api().delete(`/member/addresses/${aid}`);
    setUser(r.data);
  };

  const reorder = (o) => {
    navigate("/coffee/customize", {
      state: { prefill: { speciality: o.speciality, roast: o.roast, grind: o.grind, size: o.size, zone: o.shipping_zone, pincode: o.destination_pincode, is_reorder: true } },
    });
  };

  if (checking || !user) {
    return (
      <div className="min-h-screen bg-[#F7F5F0] flex items-center justify-center text-[#5C665A]">
        <Loader2 size={18} className="animate-spin mr-2" /> Loading your dashboard…
      </div>
    );
  }

  const fmtDate = (s) => new Date(s).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  const statusPill = (st) => (
    <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${st === "paid" ? "bg-[#89937C]/15 text-[#2D372B]" : "bg-[#AD6A48]/15 text-[#8A5236]"}`}>{st}</span>
  );

  return (
    <div className="min-h-screen bg-[#F7F5F0] px-6 py-10">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#5C665A] hover:text-[#2D372B]">
            <ArrowLeft size={16} /> Back to site
          </Link>
          <button data-testid="member-logout-btn" onClick={logout} className="inline-flex items-center gap-2 text-sm text-[#5C665A] hover:text-[#2D372B]">
            <LogOut size={14} /> Sign out
          </button>
        </div>

        <div data-testid="member-dashboard" className="flex items-center gap-4 mb-10">
          {user.picture ? (
            <img src={user.picture} alt="" className="w-14 h-14 rounded-full object-cover" referrerPolicy="no-referrer" />
          ) : (
            <div className="w-14 h-14 rounded-full bg-[#89937C]/15 flex items-center justify-center"><UserRound size={22} className="text-[#89937C]" /></div>
          )}
          <div>
            <h1 className="font-serif-display text-3xl sm:text-4xl text-[#2D372B]">Hey, {(user.name || user.email).split(" ")[0]}</h1>
            <p className="text-sm text-[#5C665A]">{user.email}</p>
          </div>
        </div>

        <div className="space-y-8">
          <LoyaltySection />
          <GlobalSeriesStrip />
          <section className="bg-white border border-[#E5E2D9] rounded-3xl p-7">
            <div className="flex items-center gap-2 mb-4"><UserRound size={16} className="text-[#89937C]" /><h2 className="font-serif-display text-xl text-[#2D372B]">Profile</h2></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Input data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" className="bg-[#F7F5F0] border-[#E5E2D9] h-11" />
              <Input data-testid="profile-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Contact number" className="bg-[#F7F5F0] border-[#E5E2D9] h-11" />
              <div>
                <label className="text-xs text-[#5C665A] block mb-1">Birthday — we'll send you a little treat 🎂</label>
                <Input data-testid="profile-dob-input" type="date" value={dob} onChange={(e) => setDob(e.target.value)} className="bg-[#F7F5F0] border-[#E5E2D9] h-11" />
              </div>
            </div>
            <button data-testid="profile-save-btn" onClick={saveProfile} disabled={savingProfile} className="mt-4 inline-flex items-center gap-2 rounded-full px-6 py-2.5 bg-[#2D372B] text-[#F7F5F0] text-sm font-medium hover:bg-[#89937C] transition-colors disabled:opacity-60">
              {savingProfile && <Loader2 size={13} className="animate-spin" />} Save profile
            </button>
          </section>

          <PasswordSection />

          <section className="bg-white border border-[#E5E2D9] rounded-3xl p-7">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2"><MapPin size={16} className="text-[#89937C]" /><h2 className="font-serif-display text-xl text-[#2D372B]">Shipping addresses</h2></div>
              <button data-testid="addr-add-toggle" onClick={() => setShowAddrForm(!showAddrForm)} className="text-sm text-[#89937C] hover:text-[#2D372B] inline-flex items-center gap-1"><Plus size={14} /> Add</button>
            </div>
            {(user.addresses || []).length === 0 && !showAddrForm && (
              <p className="text-sm text-[#5C665A]">No addresses yet — add one to speed up checkout, or save family & friends' addresses for gifting.</p>
            )}
            <div className="space-y-3">
              {(user.addresses || []).map((a) => (
                <div key={a.id} data-testid="address-card" className={`rounded-2xl border p-4 ${a.is_default ? "border-[#89937C] bg-[#89937C]/5" : "border-[#E5E2D9]"}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-sm font-medium text-[#2D372B]">
                      {a.label} {a.is_default && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-[#89937C] text-white">Default shipping</span>}
                    </div>
                    <div className="flex items-center gap-3">
                      {!a.is_default && (
                        <button data-testid="addr-default-btn" onClick={() => setDefault(a.id)} className="text-xs text-[#89937C] hover:text-[#2D372B] inline-flex items-center gap-1"><Star size={12} /> Set as default</button>
                      )}
                      <button data-testid="addr-delete-btn" onClick={() => deleteAddr(a.id)} className="text-[#5C665A] hover:text-[#AD6A48]"><Trash2 size={14} /></button>
                    </div>
                  </div>
                  <p className="text-sm text-[#5C665A] mt-1">{[a.line1, a.line2, a.city, a.state, a.pincode, a.country].filter(Boolean).join(", ")}{a.phone ? ` · ${a.phone}` : ""}</p>
                </div>
              ))}
            </div>
            {showAddrForm && <AddressForm onSaved={(u) => { setUser(u); setShowAddrForm(false); }} />}
          </section>

          <SubscriptionSection user={user} />

          <JourneysSection />

          <RequestsSection />

          <section className="bg-white border border-[#E5E2D9] rounded-3xl p-7">
            <div className="flex items-center gap-2 mb-4"><Coffee size={16} className="text-[#89937C]" /><h2 className="font-serif-display text-xl text-[#2D372B]">Coffee orders</h2></div>
            {!orders ? (
              <Loader2 size={16} className="animate-spin text-[#89937C]" />
            ) : orders.coffee.length === 0 ? (
              <p className="text-sm text-[#5C665A]">No coffee orders yet — <Link to="/coffee/customize" className="text-[#89937C] underline">build your first bag</Link>. Orders placed while signed in appear here.</p>
            ) : (
              <div className="space-y-3">
                {orders.coffee.map((o) => (
                  <div key={o.id} data-testid="coffee-order-row" className="rounded-2xl border border-[#E5E2D9] p-4 flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <div className="text-sm font-medium text-[#2D372B] capitalize">{(o.speciality || "").replace("_", " ")} · {o.roast} roast · {o.grind.replace("_", " ")} · {o.size >= 1000 ? `${o.size / 1000} kg` : `${o.size} g`}</div>
                      <div className="text-xs text-[#5C665A] mt-0.5">{fmtDate(o.created_at)} · ₹{o.total} {statusPill(o.payment_status)}</div>
                    </div>
                    <button data-testid="reorder-btn" onClick={() => reorder(o)} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 bg-[#2D372B] text-[#F7F5F0] text-xs font-medium hover:bg-[#89937C] transition-colors">
                      <RotateCcw size={12} /> Re-order
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="bg-white border border-[#E5E2D9] rounded-3xl p-7">
            <div className="flex items-center gap-2 mb-4"><Dumbbell size={16} className="text-[#89937C]" /><h2 className="font-serif-display text-xl text-[#2D372B]">Bootcamps & training</h2></div>
            {!orders ? (
              <Loader2 size={16} className="animate-spin text-[#89937C]" />
            ) : orders.bootcamps.length === 0 ? (
              <p className="text-sm text-[#5C665A]">No bootcamp registrations yet — <Link to="/#bootcamps" className="text-[#89937C] underline">see upcoming bootcamps</Link>.</p>
            ) : (
              <div className="space-y-3">
                {orders.bootcamps.map((b) => (
                  <div key={b.id} data-testid="bootcamp-order-row" className="rounded-2xl border border-[#E5E2D9] p-4">
                    <div className="text-sm font-medium text-[#2D372B] capitalize">{(b.bootcamp_slug || "").replace(/-/g, " ")}</div>
                    <div className="text-xs text-[#5C665A] mt-0.5">{fmtDate(b.created_at)} {statusPill(b.payment_status || "pending")}</div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
